/**
 * Short-lived browse → tenant session attribution (spec §7.4 / §7.5).
 * Session-only; bound to target tenant; TTL exactly 30 minutes.
 */
import type { CustomerAcquisitionSource } from './resolveCustomerAcquisitionSource.js';

export const DISCOVERY_ATTRIBUTION_SESSION_KEY =
  'customer:discovery:attributionContext' as const;

export const DISCOVERY_ATTRIBUTION_TTL_MS = 30 * 60 * 1000;

export interface DiscoveryAttributionContextRecord {
  readonly searchRequestId: string;
  readonly source: CustomerAcquisitionSource;
  readonly targetTenantCode: string;
  readonly createdAtMs: number;
}

type DiscoveryAttributionStore = Record<string, DiscoveryAttributionContextRecord>;

function getSessionStorage(): Storage | null {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return null;
  }
  return window.sessionStorage;
}

function readStore(): DiscoveryAttributionStore {
  const storage = getSessionStorage();
  if (storage === null) {
    return {};
  }
  try {
    const raw = storage.getItem(DISCOVERY_ATTRIBUTION_SESSION_KEY);
    if (raw === null || raw.trim().length === 0) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }
    return parsed as DiscoveryAttributionStore;
  } catch {
    return {};
  }
}

function writeStore(store: DiscoveryAttributionStore): void {
  const storage = getSessionStorage();
  if (storage === null) {
    return;
  }
  if (Object.keys(store).length === 0) {
    storage.removeItem(DISCOVERY_ATTRIBUTION_SESSION_KEY);
    return;
  }
  storage.setItem(DISCOVERY_ATTRIBUTION_SESSION_KEY, JSON.stringify(store));
}

function normalizeTenantCode(tenantCode: string): string {
  return tenantCode.trim().toLowerCase();
}

function isExpired(record: DiscoveryAttributionContextRecord, nowMs: number): boolean {
  return nowMs - record.createdAtMs > DISCOVERY_ATTRIBUTION_TTL_MS;
}

function pruneExpired(store: DiscoveryAttributionStore, nowMs: number): DiscoveryAttributionStore {
  const next: DiscoveryAttributionStore = {};
  for (const [key, record] of Object.entries(store)) {
    if (!isExpired(record, nowMs)) {
      next[key] = record;
    }
  }
  return next;
}

export function saveDiscoveryAttributionContext(
  input: Omit<DiscoveryAttributionContextRecord, 'createdAtMs'> & {
    readonly createdAtMs?: number;
  },
): void {
  const tenantKey = normalizeTenantCode(input.targetTenantCode);
  if (tenantKey.length === 0) {
    return;
  }
  const nowMs = input.createdAtMs ?? Date.now();
  const store = pruneExpired(readStore(), nowMs);
  store[tenantKey] = {
    searchRequestId: input.searchRequestId,
    source: input.source,
    targetTenantCode: tenantKey,
    createdAtMs: nowMs,
  };
  writeStore(store);
}

export function peekDiscoveryAttributionContext(
  targetTenantCode: string,
  nowMs: number = Date.now(),
): DiscoveryAttributionContextRecord | null {
  const tenantKey = normalizeTenantCode(targetTenantCode);
  if (tenantKey.length === 0) {
    return null;
  }
  const store = pruneExpired(readStore(), nowMs);
  writeStore(store);
  const record = store[tenantKey];
  if (record === undefined) {
    return null;
  }
  if (record.targetTenantCode !== tenantKey) {
    return null;
  }
  return record;
}

/** Returns matching context for the tenant and removes it (one-shot bind). */
export function consumeDiscoveryAttributionContext(
  targetTenantCode: string,
  nowMs: number = Date.now(),
): DiscoveryAttributionContextRecord | null {
  const tenantKey = normalizeTenantCode(targetTenantCode);
  if (tenantKey.length === 0) {
    return null;
  }
  const store = pruneExpired(readStore(), nowMs);
  const record = store[tenantKey];
  if (record === undefined || record.targetTenantCode !== tenantKey) {
    writeStore(store);
    return null;
  }
  delete store[tenantKey];
  writeStore(store);
  return record;
}

export function clearDiscoveryAttributionContext(targetTenantCode?: string): void {
  if (targetTenantCode === undefined) {
    writeStore({});
    return;
  }
  const tenantKey = normalizeTenantCode(targetTenantCode);
  const store = readStore();
  if (store[tenantKey] === undefined) {
    return;
  }
  delete store[tenantKey];
  writeStore(store);
}
